import React, { useEffect, useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import MenuDrawer from './MenuDrawer';
import AuthModal from './AuthModal';
import { CloseIcon, CartIcon } from './Icons';

export default function JournalPage({
  view,
  setView,
  isMenuOpen,
  setIsMenuOpen,
  isCartOpen,
  setIsCartOpen,
  isBookingOpen,
  setIsBookingOpen,
  isSearchOpen,
  setIsSearchOpen,
  currentUser,
  setCurrentUser,
  wishlist = [],
  cartItems = [],
  setCartItems,
  targetSection,
  setTargetSection,
}) {
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [highlightPreserving, setHighlightPreserving] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Monitor scroll for mobile back-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to section when requested (e.g. from Client Diaries)
  useEffect(() => {
    if (targetSection === 'preserving-your-jewellery') {
      const scrollTimer = setTimeout(() => {
        const el = document.getElementById('preserving-your-jewellery');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setHighlightPreserving(true);
          setTimeout(() => setHighlightPreserving(false), 3000);
        }
      }, 150);
      return () => clearTimeout(scrollTimer);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [targetSection]);

  const scrollToAnchor = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleQtyChange = (id, delta) => {
    setCartItems(prev => {
      return prev.map(item => {
        if (String(item.id) === String(id)) {
          const newQty = item.qty + delta;
          return newQty > 0 ? { ...item, qty: newQty } : null;
        }
        return item;
      }).filter(Boolean);
    });
  };

  const removeItem = (id) => {
    setCartItems(prev => prev.filter(i => String(i.id) !== String(id)));
  };

  const getSubtotal = () => {
    return cartItems.reduce((acc, item) => acc + (item.price * item.qty), 0);
  };

  return (
    <div className="bg-noise min-h-screen font-sans text-[#584738] flex flex-col antialiased">
      {/* ── HEADER ── */}
      <Header
        setView={setView}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        setIsSearchOpen={setIsSearchOpen}
        setIsAuthOpen={setIsAuthOpen}
        setIsCartOpen={setIsCartOpen}
        cartItems={cartItems}
      />

      {/* ── MAIN CONTENT ── */}
      <main className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-8 md:px-12 py-6 sm:py-8 md:py-14">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-[10px] sm:text-[10.5px] font-sans tracking-widest uppercase text-[#584738]/50 mb-6 sm:mb-8" aria-label="Breadcrumb">
          <button onClick={() => setView('home')} className="hover:text-[#584738] transition-colors focus:outline-none cursor-pointer">
            Home
          </button>
          <span>/</span>
          <span className="text-[#584738]/50">Journal</span>
          <span>/</span>
          <span className="text-[#584738] font-semibold">Care Guide</span>
        </nav>

        {/* Hero Title Section */}
        <header className="text-center max-w-3xl mx-auto mb-10 sm:mb-14 md:mb-16">
          <span className="inline-block text-[10px] sm:text-[10.5px] font-sans uppercase tracking-[0.25em] text-[#B59E7D] font-bold mb-2.5 sm:mb-3">
            The Journal &bull; Care Guide &amp; Client Diaries
          </span>
          <h1 className="font-serif text-2xl sm:text-3xl md:text-5xl text-[#584738] font-normal tracking-wide leading-tight mb-4 sm:mb-6">
            House of Karnnikaraa Jewellery Care Guide
          </h1>
          <div className="w-16 h-0.5 bg-[#B59E7D]/60 mx-auto mb-4 sm:mb-6"></div>
          <p className="font-serif text-[14px] sm:text-base md:text-lg text-[#584738]/85 leading-relaxed font-light italic px-1">
            Every creation by House of Karnnikaraa is crafted in 925 Sterling Silver, thoughtfully designed to be treasured for years to come. Sterling silver is a precious metal that naturally evolves with time, developing a subtle patina unique to its journey. With mindful use, proper care and regular upkeep, your jewellery will continue to retain its beauty and brilliance.
          </p>
        </header>

        {/* Quick Table of Contents / Mobile Swipeable Pills */}
        <div className="bg-[#FAF8F5] border border-[#584738]/10 rounded-sm p-3.5 sm:p-5 mb-10 sm:mb-14 shadow-xs">
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[10.5px] sm:text-[11px] font-sans font-bold uppercase tracking-[0.18em] text-[#584738]/70">
              In This Guide
            </p>
            <span className="sm:hidden text-[9px] font-sans text-[#B59E7D] uppercase tracking-widest font-semibold">Swipe &rarr;</span>
          </div>
          <div className="flex overflow-x-auto no-scrollbar sm:flex-wrap gap-2 pb-1 -mx-3.5 px-3.5 sm:mx-0 sm:px-0 text-xs font-sans">
            <button
              onClick={() => scrollToAnchor('care-essentials')}
              className="whitespace-nowrap shrink-0 px-3.5 py-1.5 bg-white border border-[#584738]/15 hover:border-[#584738] active:bg-[#584738] active:text-white text-[#584738] transition-colors cursor-pointer text-[10.5px] sm:text-[11px] tracking-wider uppercase font-medium rounded-full shadow-xs"
            >
              Care Essentials
            </button>
            <button
              onClick={() => scrollToAnchor('preserving-your-jewellery')}
              className="whitespace-nowrap shrink-0 px-3.5 py-1.5 bg-white border border-[#584738]/15 hover:border-[#584738] active:bg-[#584738] active:text-white text-[#584738] transition-colors cursor-pointer text-[10.5px] sm:text-[11px] tracking-wider uppercase font-medium rounded-full shadow-xs"
            >
              Preserving Your Jewellery
            </button>
            <button
              onClick={() => scrollToAnchor('maintaining-brilliance')}
              className="whitespace-nowrap shrink-0 px-3.5 py-1.5 bg-white border border-[#584738]/15 hover:border-[#584738] active:bg-[#584738] active:text-white text-[#584738] transition-colors cursor-pointer text-[10.5px] sm:text-[11px] tracking-wider uppercase font-medium rounded-full shadow-xs"
            >
              Maintaining Brilliance
            </button>
            <button
              onClick={() => scrollToAnchor('collection-care')}
              className="whitespace-nowrap shrink-0 px-3.5 py-1.5 bg-white border border-[#584738]/15 hover:border-[#584738] active:bg-[#584738] active:text-white text-[#584738] transition-colors cursor-pointer text-[10.5px] sm:text-[11px] tracking-wider uppercase font-medium rounded-full shadow-xs"
            >
              Collection Care
            </button>
            <button
              onClick={() => scrollToAnchor('aftercare-services')}
              className="whitespace-nowrap shrink-0 px-3.5 py-1.5 bg-white border border-[#584738]/15 hover:border-[#584738] active:bg-[#584738] active:text-white text-[#584738] transition-colors cursor-pointer text-[10.5px] sm:text-[11px] tracking-wider uppercase font-medium rounded-full shadow-xs"
            >
              Aftercare Services
            </button>
          </div>
        </div>

        {/* ── SECTION 1: CARE ESSENTIALS ── */}
        <section id="care-essentials" className="mb-10 sm:mb-14 scroll-mt-20 md:scroll-mt-24">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <span className="w-7 h-7 rounded-full bg-[#584738]/10 text-[#584738] flex items-center justify-center font-serif text-sm font-semibold shrink-0">1</span>
            <h2 className="font-serif text-xl sm:text-2xl md:text-3xl text-[#584738] font-normal tracking-wide">
              Care Essentials
            </h2>
          </div>
          <div className="bg-white border border-[#584738]/10 p-4 sm:p-6 md:p-8 rounded-sm shadow-xs">
            <ul className="space-y-3.5 sm:space-y-4 font-sans text-xs sm:text-sm md:text-[14.5px] text-[#584738]/85 leading-relaxed">
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B59E7D] mt-1.5 sm:mt-2 shrink-0"></span>
                <span>Wear your jewellery after applying perfume, makeup, lotions and hairspray, ensuring these products have fully dried before wearing your piece.</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B59E7D] mt-1.5 sm:mt-2 shrink-0"></span>
                <span>Remove your jewellery before bathing, swimming, exercising, gardening or performing household chores.</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B59E7D] mt-1.5 sm:mt-2 shrink-0"></span>
                <span>Avoid contact with chlorine, salt water, detergents, bleach, sanitisers and other harsh chemicals, as these may affect the finish of your jewellery.</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B59E7D] mt-1.5 sm:mt-2 shrink-0"></span>
                <span>Protect your jewellery from excessive heat, humidity and prolonged exposure to direct sunlight.</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B59E7D] mt-1.5 sm:mt-2 shrink-0"></span>
                <span>Handle gemstone, enamel and ceramic jewellery with care, avoiding impact or pressure that may damage delicate elements.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* ── SECTION 2: PRESERVING YOUR JEWELLERY (TARGET OF CLIENT DIARIES) ── */}
        <section
          id="preserving-your-jewellery"
          className={`mb-10 sm:mb-14 scroll-mt-20 md:scroll-mt-24 transition-all duration-700 p-1 sm:p-2 md:p-4 rounded-sm ${
            highlightPreserving ? 'bg-[#B59E7D]/15 ring-2 ring-[#B59E7D]' : ''
          }`}
        >
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4 sm:mb-6">
            <div className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-[#584738] text-white flex items-center justify-center font-serif text-sm font-semibold shrink-0">2</span>
              <h2 className="font-serif text-xl sm:text-2xl md:text-3xl text-[#584738] font-normal tracking-wide">
                Preserving Your Jewellery
              </h2>
            </div>
            <span className="text-[9px] sm:text-[10px] uppercase font-sans tracking-[0.18em] bg-[#584738] text-[#F5E6D8] px-2.5 sm:px-3 py-1 rounded-full font-bold">
              Client Diaries &bull; Preservation
            </span>
          </div>

          <div className="bg-[#FAF8F5] border-2 border-[#584738]/15 p-4 sm:p-6 md:p-8 rounded-sm shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-[#B59E7D]/10 rounded-bl-full pointer-events-none"></div>
            <p className="font-serif text-xs sm:text-sm md:text-base text-[#584738]/90 italic mb-4 sm:mb-5 leading-relaxed">
              Proper preservation ensures that every story and milestone celebrated by your jewellery remains pristine across generations.
            </p>
            <ul className="space-y-3.5 sm:space-y-4 font-sans text-xs sm:text-sm md:text-[14.5px] text-[#584738]/85 leading-relaxed">
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-2 h-2 rounded-full bg-[#584738] mt-1.5 sm:mt-2 shrink-0"></span>
                <span><strong>Individual Storage:</strong> Store each piece individually in the House of Karnnikaraa jewellery box or pouch provided to prevent scratches and tangling.</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-2 h-2 rounded-full bg-[#584738] mt-1.5 sm:mt-2 shrink-0"></span>
                <span><strong>Climate Consideration:</strong> Keep your jewellery in a cool, dry place away from moisture and humidity.</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-2 h-2 rounded-full bg-[#584738] mt-1.5 sm:mt-2 shrink-0"></span>
                <span><strong>Long-Term Storage:</strong> When not in use for an extended period, store your jewellery in an airtight pouch or container to help minimise the natural tarnishing of sterling silver.</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-2 h-2 rounded-full bg-[#584738] mt-1.5 sm:mt-2 shrink-0"></span>
                <span><strong>Separate Delicate Materials:</strong> Avoid storing multiple jewellery pieces together, particularly those featuring Polki, Meenakari and Jaipur Blue Pottery details.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* ── SECTION 3: MAINTAINING ITS BRILLIANCE ── */}
        <section id="maintaining-brilliance" className="mb-10 sm:mb-14 scroll-mt-20 md:scroll-mt-24">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <span className="w-7 h-7 rounded-full bg-[#584738]/10 text-[#584738] flex items-center justify-center font-serif text-sm font-semibold shrink-0">3</span>
            <h2 className="font-serif text-xl sm:text-2xl md:text-3xl text-[#584738] font-normal tracking-wide">
              Maintaining Its Brilliance
            </h2>
          </div>
          <div className="bg-white border border-[#584738]/10 p-4 sm:p-6 md:p-8 rounded-sm shadow-xs">
            <ul className="space-y-3.5 sm:space-y-4 font-sans text-xs sm:text-sm md:text-[14.5px] text-[#584738]/85 leading-relaxed">
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B59E7D] mt-1.5 sm:mt-2 shrink-0"></span>
                <span>After every wear, gently wipe your jewellery with a soft, lint-free or microfibre cloth to remove natural oils, moisture and dust.</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B59E7D] mt-1.5 sm:mt-2 shrink-0"></span>
                <span>Sterling silver may naturally tarnish over time. This is an authentic characteristic of precious silver that can always be revitalised.</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B59E7D] mt-1.5 sm:mt-2 shrink-0"></span>
                <span>Avoid abrasive cleaning agents, rough brushes, chemical dips or ultrasonic cleaners unless specifically recommended for your jewellery.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* ── SECTION 4: COLLECTION-SPECIFIC CARE ── */}
        <section id="collection-care" className="mb-10 sm:mb-14 scroll-mt-20 md:scroll-mt-24">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <span className="w-7 h-7 rounded-full bg-[#584738]/10 text-[#584738] flex items-center justify-center font-serif text-sm font-semibold shrink-0">4</span>
            <h2 className="font-serif text-xl sm:text-2xl md:text-3xl text-[#584738] font-normal tracking-wide">
              Collection-Specific Care
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 sm:gap-5">
            {/* Polki Heirlooms */}
            <div className="bg-white border border-[#584738]/10 border-l-2 border-l-[#B59E7D]/60 hover:border-l-[#B59E7D] p-4 sm:p-6 rounded-sm shadow-xs transition-colors">
              <span className="text-[9.5px] sm:text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#B59E7D] block mb-1">Collection 01</span>
              <h3 className="font-serif text-base sm:text-lg text-[#584738] font-semibold mb-2">Polki Heirlooms</h3>
              <p className="font-sans text-xs sm:text-sm text-[#584738]/80 leading-relaxed">
                Polki jewellery features delicate uncut stones that should be handled with extra care. Protect the jewellery from impact, moisture and chemicals, and always store each piece separately to preserve its setting.
              </p>
            </div>

            {/* Classic CZ */}
            <div className="bg-white border border-[#584738]/10 border-l-2 border-l-[#B59E7D]/60 hover:border-l-[#B59E7D] p-4 sm:p-6 rounded-sm shadow-xs transition-colors">
              <span className="text-[9.5px] sm:text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#B59E7D] block mb-1">Collection 02</span>
              <h3 className="font-serif text-base sm:text-lg text-[#584738] font-semibold mb-2">Classic CZ</h3>
              <p className="font-sans text-xs sm:text-sm text-[#584738]/80 leading-relaxed">
                To maintain the brilliance of Cubic Zirconia, clean gently with a soft cloth and avoid abrasive surfaces that may scratch the stones.
              </p>
            </div>

            {/* Eternal Moissanite */}
            <div className="bg-white border border-[#584738]/10 border-l-2 border-l-[#B59E7D]/60 hover:border-l-[#B59E7D] p-4 sm:p-6 rounded-sm shadow-xs transition-colors">
              <span className="text-[9.5px] sm:text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#B59E7D] block mb-1">Collection 03</span>
              <h3 className="font-serif text-base sm:text-lg text-[#584738] font-semibold mb-2">Eternal Moissanite</h3>
              <p className="font-sans text-xs sm:text-sm text-[#584738]/80 leading-relaxed">
                Moissanite is renowned for its exceptional brilliance and durability. Keeping in its own box will help preserve its natural sparkle.
              </p>
            </div>

            {/* Royal Meenakari */}
            <div className="bg-white border border-[#584738]/10 border-l-2 border-l-[#B59E7D]/60 hover:border-l-[#B59E7D] p-4 sm:p-6 rounded-sm shadow-xs transition-colors">
              <span className="text-[9.5px] sm:text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#B59E7D] block mb-1">Collection 04</span>
              <h3 className="font-serif text-base sm:text-lg text-[#584738] font-semibold mb-2">Royal Meenakari</h3>
              <p className="font-sans text-xs sm:text-sm text-[#584738]/80 leading-relaxed">
                The intricate Meenakari enamel work should be protected from impact, perfumes, cosmetics and prolonged exposure to moisture to preserve its colour and finish.
              </p>
            </div>

            {/* Jaipur Blue Pottery */}
            <div className="bg-white border border-[#584738]/10 border-l-2 border-l-[#B59E7D]/60 hover:border-l-[#B59E7D] p-4 sm:p-6 rounded-sm shadow-xs transition-colors">
              <span className="text-[9.5px] sm:text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#B59E7D] block mb-1">Collection 05</span>
              <h3 className="font-serif text-base sm:text-lg text-[#584738] font-semibold mb-2">Jaipur Blue Pottery</h3>
              <p className="font-sans text-xs sm:text-sm text-[#584738]/80 leading-relaxed">
                Inspired by Jaipur’s iconic Blue Pottery tradition, these creations feature delicate ceramic elements. Handle them with care and avoid dropping or exposing them to sudden impact, as the ceramic components may chip or crack.
              </p>
            </div>

            {/* Minimalist Luxe */}
            <div className="bg-white border border-[#584738]/10 border-l-2 border-l-[#B59E7D]/60 hover:border-l-[#B59E7D] p-4 sm:p-6 rounded-sm shadow-xs transition-colors">
              <span className="text-[9.5px] sm:text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#B59E7D] block mb-1">Collection 06</span>
              <h3 className="font-serif text-base sm:text-lg text-[#584738] font-semibold mb-2">Minimalist Luxe</h3>
              <p className="font-sans text-xs sm:text-sm text-[#584738]/80 leading-relaxed">
                Designed for everyday elegance, these pieces should still be removed before sleeping, exercising or engaging in activities that may subject them to scratches or unnecessary pressure.
              </p>
            </div>
          </div>
        </section>

        {/* ── SECTION 5: AFTERCARE SERVICES ── */}
        <section id="aftercare-services" className="mb-10 sm:mb-14 scroll-mt-20 md:scroll-mt-24">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <span className="w-7 h-7 rounded-full bg-[#584738]/10 text-[#584738] flex items-center justify-center font-serif text-sm font-semibold shrink-0">5</span>
            <h2 className="font-serif text-xl sm:text-2xl md:text-3xl text-[#584738] font-normal tracking-wide">
              Aftercare Services
            </h2>
          </div>
          
          <div className="bg-white border border-[#584738]/10 p-4 sm:p-6 md:p-8 rounded-sm shadow-xs">
            <ul className="space-y-3.5 sm:space-y-4 font-sans text-xs sm:text-sm md:text-[14.5px] text-[#584738]/85 leading-relaxed mb-6 sm:mb-8">
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B59E7D] mt-1.5 sm:mt-2 shrink-0"></span>
                <span>Periodically inspect clasps, chains, settings and fastenings to ensure they remain secure.</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B59E7D] mt-1.5 sm:mt-2 shrink-0"></span>
                <span>Sterling silver naturally develops a patina over time due to exposure to air, moisture and everyday wear. This is a natural characteristic of the metal and can be restored with appropriate care.</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B59E7D] mt-1.5 sm:mt-2 shrink-0"></span>
                <span>For professional polishing, repairs, resizing or restoration, we recommend seeking assistance from an experienced jewellery specialist.</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B59E7D] mt-1.5 sm:mt-2 shrink-0"></span>
                <span>Should your House of Karnnikaraa creation require expert attention, our team will be delighted to assist you with guidance on its care, maintenance and restoration. Simply reach out to us, and we will help ensure your cherished jewellery continues to be enjoyed for years to come.</span>
              </li>
            </ul>

            <div className="p-4 sm:p-5 bg-[#FAF8F5] border border-[#584738]/10 rounded-sm flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
              <div>
                <p className="font-serif text-sm sm:text-base text-[#584738] font-medium">Need Assistance With Your Creation?</p>
                <p className="font-sans text-[11px] sm:text-xs text-[#584738]/60 mt-0.5">Our atelier specialists in Jaipur are available for care consultation.</p>
              </div>
              <button
                onClick={() => setIsBookingOpen(true)}
                className="w-full sm:w-auto bg-[#584738] hover:bg-black text-[#F5E6D8] px-6 py-2.5 sm:py-3 text-[10.5px] sm:text-[11px] font-sans uppercase font-bold tracking-widest transition-colors shrink-0 shadow-xs cursor-pointer active:scale-95"
              >
                Book Appointment &rarr;
              </button>
            </div>
          </div>
        </section>

        {/* Closing Note Banner */}
        <div className="text-center py-8 sm:py-10 px-4 sm:px-6 bg-[#FAF8F5] border border-[#584738]/10 rounded-sm shadow-xs">
          <p className="font-serif text-sm sm:text-base md:text-lg text-[#584738] italic max-w-2xl mx-auto leading-relaxed">
            &ldquo;With thoughtful care and mindful handling, your House of Karnnikaraa jewellery will continue to celebrate your moments, preserving its elegance and becoming a cherished part of your collection for generations.&rdquo;
          </p>
        </div>
      </main>

      {/* Floating Back to Top Button for Mobile / Long Reads */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-5 z-40 bg-[#584738] text-[#F5E6D8] border border-[#B59E7D]/40 p-2.5 sm:px-3 sm:py-2 rounded-full shadow-xl hover:bg-black active:scale-90 transition-all cursor-pointer flex items-center justify-center gap-1.5 text-xs font-sans font-medium"
          aria-label="Back to Top"
        >
          <svg className="w-4 h-4 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
          </svg>
          <span className="hidden sm:inline text-[10px] uppercase tracking-wider pr-0.5">Top</span>
        </button>
      )}

      {/* ── FOOTER ── */}
      <Footer setView={setView} />

      {/* ── MENU DRAWER ── */}
      <MenuDrawer
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        setView={setView}
        setIsBookingOpen={setIsBookingOpen}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
        setIsAuthOpen={setIsAuthOpen}
        wishlist={wishlist}
        setJournalSection={setTargetSection}
      />

      {/* ── AUTH MODAL ── */}
      <AuthModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
      />

      {/* ── CART DRAWER ── */}
      <div className={`fixed inset-x-0 bottom-0 top-[70px] md:top-[75px] z-50 transition-opacity duration-300 pointer-events-none ${isCartOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0'}`}>
        <div onClick={() => setIsCartOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-xs" />
        <div className={`fixed inset-y-0 right-0 h-full w-full md:w-96 bg-noise shadow-2xl p-6 transition-transform duration-300 flex flex-col ${isCartOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between border-b border-[#584738]/10 pb-4 mb-6 flex-shrink-0">
            <h3 className="font-serif text-lg tracking-widest font-semibold uppercase flex items-center gap-2 text-[#584738]">
              Your Cart
              <span className="text-xs bg-[#584738]/10 px-2.5 py-0.5 rounded-full font-sans font-bold">{cartItems.reduce((a, i) => a + i.qty, 0)}</span>
            </h3>
            <button onClick={() => setIsCartOpen(false)} className="focus:outline-none cursor-pointer" aria-label="Close Cart"><CloseIcon /></button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1 scrollbar-thin">
            {cartItems.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 text-[#584738]/40 font-serif">
                <CartIcon />
                <p className="mt-3 text-sm tracking-wide font-medium">Your cart is empty.</p>
              </div>
            ) : (
              cartItems.map((item, idx) => (
                <div key={`${item.id}-${idx}`} className="flex gap-4 border-b border-[#584738]/5 pb-4 font-serif">
                  <img src={item.image} alt={item.name} className="w-16 h-20 object-cover border border-[#584738]/5" />
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="text-sm font-serif font-medium tracking-wide text-[#584738]/90 leading-tight">{item.name}</h4>
                      <p className="text-[12.5px] text-[#584738] font-medium mt-1">₹{item.price.toLocaleString('en-IN')}</p>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-2 font-sans">
                      <div className="flex items-center border border-[#584738]/20 select-none">
                        <button onClick={() => handleQtyChange(item.id, -1)} className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none cursor-pointer">-</button>
                        <span className="px-3 py-0.5 text-center font-medium">{item.qty}</span>
                        <button onClick={() => handleQtyChange(item.id, 1)} className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none cursor-pointer">+</button>
                      </div>
                      <button onClick={() => removeItem(item.id)} className="text-[#584738]/60 hover:text-red-500 font-semibold cursor-pointer">Remove</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          {cartItems.length > 0 && (
            <div className="border-t border-[#584738]/10 pt-6 mt-6 flex flex-col gap-4 flex-shrink-0">
              <div className="flex items-center justify-between font-serif">
                <span className="text-md font-medium tracking-wide text-[#584738]/80">Subtotal</span>
                <span className="text-lg font-bold text-[#584738]">₹{getSubtotal().toLocaleString('en-IN')}</span>
              </div>
              <button onClick={() => { setIsCartOpen(false); setView('checkout'); }} className="w-full bg-[#584738] text-white hover:bg-black py-3.5 text-xs tracking-widest font-bold uppercase transition-colors shadow-xs font-sans cursor-pointer">
                PROCEED TO CHECKOUT
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
